import { useQueries, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  fetchBalances,
  fetchDelegatorValidators,
  fetchNftListByOwner,
} from '@/api'
import { useNftMetadataMap } from '@/pages/Home/hooks/nft-metadata'
import { queryKeys } from '@/queries/keys'

import { coinAmountFromBalances, computeDelegationState } from '../utils'
import { useBecomeValidatorBase } from './use-become-validator-base'

export function useBecomeValidatorShardsPayload(
  address: string | null,
  selectedAddresses: string[],
) {
  const base = useBecomeValidatorBase()

  // owned NFTs + metadata + validator checks
  const { data: ownedNftsResponse, isLoading: isLoadingOwnedNfts } = useQuery({
    queryKey: queryKeys.nft.listByOwner(address, { limit: '1000' }),
    enabled: Boolean(address),
    queryFn: async () =>
      address
        ? fetchNftListByOwner(address, { limit: '1000' })
        : { nfts: [], pagination: { next_key: null, total: '0' } },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const ownedNfts = useMemo(
    () => ownedNftsResponse?.nfts ?? [],
    [ownedNftsResponse],
  )

  const metadataMap = useNftMetadataMap(ownedNfts.map(n => n.uri))

  const validatorChecks = useQueries({
    queries: ownedNfts.map(n => ({
      queryKey: queryKeys.staking.validatorCheck(n.address),
      queryFn: async () => fetchDelegatorValidators(n.address),
      staleTime: 60_000,
      enabled: ownedNfts.length > 0,
    })),
  })

  const isCheckingValidators = validatorChecks.some(q => q.isLoading)

  const eligibleNfts = useMemo(() => {
    if (validatorChecks.length === 0) return []
    return ownedNfts.filter((_, index) => {
      const validators = validatorChecks[index]?.data
      return Array.isArray(validators) && validators.length === 0
    })
  }, [ownedNfts, validatorChecks])

  // selected balances
  const selectedBalancesQueries = useQueries({
    queries: selectedAddresses.map(addr => ({
      queryKey: queryKeys.bank.balances(addr),
      enabled: selectedAddresses.length > 0,
      queryFn: async () => fetchBalances(addr),
      staleTime: 30_000,
    })),
  })

  const isLoadingSelectedBalances = selectedBalancesQueries.some(
    q => q.isLoading,
  )

  const selectedTotalAtomic = useMemo(() => {
    if (!selectedBalancesQueries.length) return '0'
    let total = 0n
    for (const q of selectedBalancesQueries) {
      const balances = q.data?.balances ?? []
      const amount = coinAmountFromBalances(balances, base.bondDenom)
      if (amount) total += BigInt(amount)
    }
    return total.toString()
  }, [selectedBalancesQueries, base.bondDenom])

  const { remainingAtomic, hasEnoughDelegation, remainingColorClass } = useMemo(
    () =>
      computeDelegationState(selectedTotalAtomic, base.minimalDelegationAmount),
    [selectedTotalAtomic, base.minimalDelegationAmount],
  )

  const isChecking = isLoadingOwnedNfts && ownedNfts.length === 0

  return {
    // base
    bondDenom: base.bondDenom,
    minimalDelegationAmount: base.minimalDelegationAmount,

    // nft-specific
    ownedNfts,
    eligibleNfts,
    metadataMap,
    isCheckingValidators,
    isChecking,

    // balances
    selectedTotalAtomic,
    remainingAtomic,
    hasEnoughDelegation,
    remainingColorClass,
    isLoadingSelectedBalances,
  }
}
